/**
 * Device pairing + iMessage account settings types (Phase 4a).
 * Mirrors apps/server/src/api/devices.ts publicDevice/publicAccount and
 * apps/server/src/api/accounts.ts PATCH /api/v1/accounts/:accountId.
 */

export type DeviceStatus = "online" | "offline";

export type PublicDevice = {
  id: string;
  name: string;
  status: DeviceStatus;
  createdAt: string;
  lastSeen?: string;
  adapterVersion?: string;
  capabilities?: string[];
  /** Full Disk Access granted (from the adapter's `status` frame). */
  fda?: boolean;
  /** Automation (AppleScript) consent granted (from the adapter's `status` frame). */
  automation?: boolean;
  /**
   * Whether Messages.app is running on this Mac (from the adapter's honest
   * `status` probe — plan § C9). May be `false` while `status === "online"`
   * (e.g. a fast-user-switched-away macOS session can still relay reads but
   * sends will fail) — that combination is what the Devices page warns on.
   */
  messagesRunning?: boolean;
  /** macOS short username reported at pairing (plan § A4), e.g. "will". */
  username?: string;
  /** Mac hostname reported at pairing (plan § A4). */
  hostname?: string;
  os?: string;
  osVersion?: string;
};

export type AccountSettings = {
  autonomousSends: boolean;
  allowedChatIds: string[];
  /** When true, 1:1 chats with people in Mac Contacts skip per-send approval. */
  allowContactsSends: boolean;
};

export type PublicAccount = {
  id: string;
  platform: "imessage";
  deviceId: string;
  label: string;
  handles: string[];
  settings: AccountSettings;
  createdAt: string;
};

/** GET /api/v1/devices response. */
export type DevicesListResponse = {
  devices: PublicDevice[];
  accounts: PublicAccount[];
};

/** POST /api/v1/devices/pair response. */
export type PairingCode = {
  code: string;
  expiresAt: string;
};

/** One allowlist entry rewritten to its canonical stored form on save. */
export type CanonicalizedEntry = { input: string; stored: string };

/** PATCH /api/v1/accounts/:accountId response. */
export type UpdateAccountSettingsResponse = {
  account: PublicAccount;
  pushed: boolean;
  canonicalized: CanonicalizedEntry[];
};

/**
 * Security-critical copy for the autonomous-sends control (shown via info icon).
 * Exact text per the D2 confirm-gate spec — do not paraphrase or shorten.
 * Covered verbatim by test/chat.test.ts.
 */
export const AUTONOMOUS_SENDS_WARNING =
  "Autonomous sends let this AI assistant send iMessages on your behalf without asking you first, to any chat on the allowlist below. If the AI's session is ever manipulated by content it reads — a prompt-injection attack hidden in a message, web page, or document — it can be tricked into sending messages you never approved, to people you never chose to message, without your review. Keep the allowlist as small as possible (ideally just your own number, for testing) and turn autonomous sends off when you are not actively using it. Leaving this off means every send waits for your approval on the Mac before anything goes out.";

/**
 * Security copy for the per-account "allow Contacts" control (info icon).
 * The setting is stored on this account only; contact lookup runs on that
 * account's paired Mac (Contacts.app for that Mac user — not a shared
 * global list across all of your accounts).
 */
export const ALLOW_CONTACTS_SENDS_WARNING =
  "For this account only: when enabled, the AI can send without asking first if every recipient is recognized — your own handles on this account, people in Contacts on the Mac paired to this account, and group chats where all members are recognized. Other accounts keep their own setting. Unrecognized people still need approval (MCP or Mac). Contacts access must be granted for that Mac's menu bar app.";

/**
 * Format canonicalized allowlist rewrites for display, e.g.
 * "any;-;+15551234567 → iMessage;-;+15551234567". Pure + unit-testable
 * independent of DOM rendering.
 */
export function formatCanonicalizedEntries(
  entries: CanonicalizedEntry[],
): string[] {
  return entries.map((e) => `${e.input} → ${e.stored}`);
}

/**
 * mm:ss countdown from `now` to `expiresAt` (ISO timestamp). Clamped to
 * "00:00" once expired, or on an unparsable timestamp.
 */
export function formatCountdown(
  expiresAt: string,
  now: number = Date.now(),
): string {
  const exp = Date.parse(expiresAt);
  if (!Number.isFinite(exp)) return "00:00";
  const totalSeconds = Math.max(0, Math.floor((exp - now) / 1000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** True once `expiresAt` has passed relative to `now`. */
export function isExpired(expiresAt: string, now: number = Date.now()): boolean {
  const exp = Date.parse(expiresAt);
  if (!Number.isFinite(exp)) return true;
  return exp <= now;
}

/** Parse a textarea's "one entry per line" allowlist input into a clean string array. */
export function parseAllowlistText(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/** Mirrors the server's PATCH /api/v1/accounts/:accountId label validation (plan § A5). */
export const MAX_ACCOUNT_LABEL_LENGTH = 64;

/** Client-side pre-check so the label field can show inline feedback before the round-trip. */
export function isValidAccountLabel(label: string): boolean {
  const trimmed = label.trim();
  return trimmed.length > 0 && trimmed.length <= MAX_ACCOUNT_LABEL_LENGTH;
}

/**
 * Display-only "possible duplicate" hint (plan § B8): flags another device
 * that reports the *same* hostname + username as `target` — the signal that
 * a re-pair created a second row for the same macOS user session instead of
 * reusing the first. No auto-dedupe; revoke is the remedy, left to the user.
 * Both fields must be present (and equal) on both sides — devices reported
 * by adapters older than this plan have neither field and never match.
 */
export function findPossibleDuplicateDevice(
  target: PublicDevice,
  others: PublicDevice[],
): PublicDevice | undefined {
  if (!target.hostname || !target.username) return undefined;
  return others.find(
    (d) =>
      d.id !== target.id &&
      d.hostname === target.hostname &&
      d.username === target.username,
  );
}
